import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  CalendarDaysIcon,
  ClockIcon,
  CameraIcon,
  ChatBubbleBottomCenterTextIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import {
  Select as AntSelect,
  DatePicker as AntDatePicker,
  TimePicker as AntTimePicker,
  Input,
} from "antd";
import dayjs from "dayjs";
import {
  closeStatus,
  commentList,
  editAlert,
  postComment,
  updateAlertStatusToInProgress,
} from "../../../services/AlertsTable";
import { toast } from "react-toastify";
import axios from "axios";

const { TextArea } = Input;

const uploadAttachment = async (file) => {
  const formData = new FormData();
  formData.append("attachment", file);

  try {
    const response = await axios.post(
      "https://godrej.app-assertai.com:5000/Other/upload-attachment-for-alerts",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data && response.data.url) {
      return response.data.url;
    } else {
      throw new Error("Upload failed: No URL returned from API");
    }
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

const AntdSelectField = ({
  value,
  options,
  placeholder,
  onChange,
  mode,
  disabled,
}) => (
  <AntSelect
    mode={mode}
    style={{ width: "100%" }}
    placeholder={placeholder}
    value={value || (mode === "multiple" ? [] : undefined)}
    onChange={onChange}
    options={options}
    allowClear
    showSearch
    optionFilterProp="label"
    className="custom-ant-select"
    getPopupContainer={(triggerNode) => triggerNode.parentNode}
    disabled={disabled}
  />
);

const AlertStatusModal = ({
  isOpen,
  onClose,
  alert,
  assignedToOptions,
  userName,
  onUpdate,
}) => {
  const [assignedTo, setAssignedTo] = useState([]);
  const [escalationTo, setEscalationTo] = useState([]);
  const [comment, setComment] = useState("");
  const [currentCommentList, setCurrentCommentList] = useState([]);
  const [status, setStatus] = useState("Open");
  const [attachments, setAttachments] = useState([]);
  const [isAssignedStatus, setIsAssignedStatus] = useState(false);
  const fileInputRef = useRef(null);

  // Manage alert status case
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState(null);
  const handleOpenModal = (action) => {
    setActionToConfirm(action);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActionToConfirm(null);
  };

  const inProgresConfirm = async () => {
    let commentsArray = currentCommentList;
    const currentAssigned = isAssignedStatus
      ? assignedTo
      : alert.alertsAssignedMails;

    try {
      if (commentsArray.length == 0) {
        return toast("Status cannot be move to in progress without comments.");
      } else {
        if (
          !currentAssigned?.includes(userName?.email) &&
          userName?.userType !== "HSE"
        ) {
          toast(
            `You cannot move this status to in progress. This alert is not assigned to ${userName?.email}. ` +
              `Only ${
                Array.isArray(currentAssigned)
                  ? currentAssigned.join(", ")
                  : currentAssigned
              } can move this alert to in-progress.`
          );
        } else {
          setStatus("InProgress");
          let updateAlertResponse = await updateAlertStatusToInProgress({
            _id: alert._id,
          });
          if (updateAlertResponse == true) {
            toast(
              "Alert Status Updated. An Email has been to your mail regarding it."
            );
            onUpdate();
            onClose();
          } else {
            toast("Alert Status Updation failed");
          }
        }
      }
    } catch (error) {}
  };

  const handleClose = async () => {
    let commentsArray = currentCommentList;
    const currentAssigned = isAssignedStatus
      ? assignedTo
      : alert.alertsAssignedMails;

    try {
      if (commentsArray?.length == 0) {
        return toast("Status cannot be close without comments.");
      } else {
        if (
          !currentAssigned?.includes(userName?.email) &&
          userName?.userType !== "HSE"
        ) {
          toast(
            `You cannot close this status. This alert is not assigned to ${userName?.email}. ` +
              `Only ${
                Array.isArray(currentAssigned)
                  ? currentAssigned.join(", ")
                  : currentAssigned
              } can close this alert.`
          );
        } else {
          setStatus("Close");
          let updateAlertResponse = await closeStatus({
            _id: alert?._id,
            name: userName?.name,
          });
          if (updateAlertResponse.status) {
            toast(
              "Alert Status Updated.An Email has been to your mail regarding it."
            );
            onUpdate();
            onClose();
          } else {
            toast("Alert Status Updation failed");
          }
          onUpdate();
          onClose();
        }
      }
    } catch (error) {}
  };

  const handleConfirmAction = () => {
    if (actionToConfirm === "close") {
      handleClose();
    } else if (actionToConfirm === "in-progress") {
      inProgresConfirm();
    }
    handleCloseModal();
  };
  const getConfirmationMessage = () => {
    if (actionToConfirm === "close") {
      return "Are you sure you want to close this status?";
    }
    if (actionToConfirm === "in-progress") {
      return "Are you sure you want to change the status to In Progress?";
    }
    return "";
  };
  // ------------------------

  const getCommentListManager = async () => {
    if (alert?._id) {
      try {
        let commentsListVal = await commentList(alert?._id);
        setCurrentCommentList(commentsListVal?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
        toast.error("Could not load previous comments.");
        setCurrentCommentList([]);
      }
    }
  };

  useEffect(() => {
    if (alert) {
      setAssignedTo(alert.alertsAssignedMails || []);
      setEscalationTo(alert.esscalationEmails || []);
      setIsAssignedStatus(alert?.status?.toLowerCase() === "assigned");
      setStatus(alert?.status);
      setComment("");
      setAttachments([]); // Reset attachments when alert changes
      getCommentListManager();
    }
  }, [alert]);

  if (!isOpen || !alert) {
    return null;
  }

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setAttachments((prev) => [...prev, ...newFiles]);
    // Reset the input value to allow selecting the same file again if removed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (indexToRemove) => {
    setAttachments((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleCommentAndUpload = async () => {
    if (!comment.trim() && attachments.length === 0) {
      toast.warn("A comment or an attachment is required.");
      return { success: false };
    }

    try {
      let uploadedUrls = [];
      if (attachments.length > 0) {
        const uploadToastId = toast.loading(
          `Uploading ${attachments.length} attachment(s)...`
        );
        const uploadPromises = attachments.map((file) =>
          uploadAttachment(file)
        );
        uploadedUrls = await Promise.all(uploadPromises);
        toast.update(uploadToastId, {
          render: "Attachments uploaded successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      }

      const newCommentPayload = {
        date: dayjs().format("ddd MMM DD YYYY"),
        time: dayjs().format("HH:mm:ss"),
        comment: comment.trim(),
        name: userName?.name,
        attachments: uploadedUrls,
      };

      const newCommentList = [newCommentPayload, ...(currentCommentList || [])];
      await postComment({ id: alert?._id, comments: newCommentList });

      setCurrentCommentList(newCommentList);
      setComment("");
      setAttachments([]);
      toast.success("Comment added successfully.");
      return { success: true };
    } catch (error) {
      console.error("Failed to post comment or upload attachments:", error);
      toast.error("Failed to add comment or upload. Please try again.");
      return { success: false };
    }
  };

  const updateEmailAssingAndEscalation = async () => {
    // 2. Handle user assignment if changed and not already assigned
    const isAssignmentChanged =
      JSON.stringify(alert.alertsAssignedMails || []) !==
        JSON.stringify(assignedTo) ||
      JSON.stringify(alert.esscalationEmails || []) !==
        JSON.stringify(escalationTo);

    if (!isAssignedStatus && isAssignmentChanged) {
      if (assignedTo?.length === 0) {
        toast.warn("Please select at least one user to assign the alert.");
        return;
      }
      try {
        await editAlert({
          id: alert._id,
          alertsAssignedMails: assignedTo,
          esscalationEmails: escalationTo,
        });
        toast.success("Alert assigned successfully.");
        onUpdate();
        setIsAssignedStatus(true);
      } catch (error) {
        console.log("error", error);
        toast.error("Failed to assign alert.");
        return; // Stop if assignment fails
      }
    }
  };

  const handleFinalSubmit = async () => {
    onUpdate();
    onClose();
  };

  const isClosedStatus =
    alert.status?.toLowerCase() === "close" ||
    alert.status?.toLowerCase() === "closed";
  const showActionButtons = !isClosedStatus;

  const handleViewImage = (event, url) => {
    event.preventDefault();
    const newTab = window.open();
    if (newTab) {
      newTab.document.title = "Image Preview";
      newTab.document.body.style.margin = "0";
      newTab.document.body.style.backgroundColor = "#111";
      newTab.document.body.style.display = "flex";
      newTab.document.body.style.alignItems = "center";
      newTab.document.body.style.justifyContent = "center";
      newTab.document.body.innerHTML = `<img src="${url}" style="max-width: 100%; max-height: 100%; object-fit: contain;" alt="Image Preview">`;
    } else {
      toast.error("Please allow popups to view the image.");
    }
  };

  return (
    <>
      <style>
        {`
          /* Styles for disabled AntSelect */
          .alert-status-modal-override .ant-select-disabled.ant-select .ant-select-selector {
              background-color: #f5f5f5 !important;
              border-color: #d9d9d9 !important;
              color: rgba(0, 0, 0, 0.88) !important;
              cursor: default !important;
          }
          .alert-status-modal-override .ant-select-disabled .ant-select-selection-item {
              color: rgba(0, 0, 0, 0.88) !important;
          }
           .alert-status-modal-override .ant-select-disabled .ant-select-clear {
              display: none !important;
          }
          /* General disabled input styles */
          .alert-status-modal-override .ant-picker-disabled,
          .alert-status-modal-override .ant-input-disabled {
              background-color: #f5f5f5 !important;
              border-color: #d9d9d9 !important;
              color: rgba(0, 0, 0, 0.88) !important;
              -webkit-text-fill-color: rgba(0, 0, 0, 0.88) !important;
              cursor: default !important;
          }
          .alert-status-modal-override .ant-picker-disabled .ant-picker-suffix,
          .alert-status-modal-override .ant-picker-disabled .ant-picker-clear {
              display: none !important;
          }
        `}
      </style>
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 alert-status-modal-override">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h2 className="text-xl font-semibold text-gray-800">
              Alert Status
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto">
            {/* Assignment Section */}
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned To
                  </label>
                  <AntdSelectField
                    mode="multiple"
                    value={assignedTo}
                    options={assignedToOptions}
                    placeholder="Select Assigned To"
                    onChange={setAssignedTo}
                    disabled={isAssignedStatus || status === "Close"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escalation To
                  </label>
                  <AntdSelectField
                    mode="multiple"
                    value={escalationTo}
                    options={assignedToOptions}
                    placeholder="Select Email To"
                    onChange={setEscalationTo}
                    disabled={isAssignedStatus || status === "Close"}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                {!isAssignedStatus && (
                  <button
                    className="mt-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150"
                    onClick={updateEmailAssingAndEscalation}
                    disabled={status === "Close"}
                  >
                    Update
                  </button>
                )}
              </div>
            </div>

            {/* Alert Info Section */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <CalendarDaysIcon className="h-6 w-6 text-teal-600" />
                  <AntDatePicker
                    value={alert.date ? dayjs(alert.date, "YYYY-MM-DD") : null}
                    format="MM/DD/YYYY"
                    disabled
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-6 w-6 text-teal-600" />
                  <AntTimePicker
                    value={alert.time ? dayjs(alert.time, "HH:mm:ss") : null}
                    format="hh:mm:ss A"
                    disabled
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <CameraIcon className="h-6 w-6 text-teal-600" />
                  <Input value={alert.camera} disabled />
                </div>
              </div>
            </div>

            {/* Comments and Upload Section */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <label className="flex items-center text-md font-medium text-gray-700 mb-2">
                <ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2" />
                Comments
              </label>
              <TextArea
                rows={3}
                placeholder="Enter Your Comments"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={status === "Close"}
              />
              <div className="flex justify-between items-center mt-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={status === "Close"}
                  className="flex items-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-300 transition-colors"
                >
                  <PaperClipIcon className="h-5 w-5 mr-2" />
                  Upload file
                </button>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*,application/pdf,video/*,.doc,.docx,.xls,.xlsx,.csv"
                />
                <button
                  className="mt-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150"
                  onClick={handleCommentAndUpload}
                  disabled={status === "Close"}
                >
                  Comment
                </button>
              </div>
              {attachments.length > 0 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    Selected files:
                  </p>
                  <ul className="space-y-1">
                    {attachments.map((file, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 flex justify-between items-center bg-white p-1 rounded"
                      >
                        <span className="truncate pr-2">{file.name}</span>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="p-1 rounded-full text-red-500 hover:bg-red-100 hover:text-red-700"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Status Section */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-40">
                  <Input value={status} disabled />
                </div>
                {showActionButtons && (
                  <>
                    <button
                      onClick={() => handleOpenModal("close")}
                      className="px-6 py-2 rounded-md font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => handleOpenModal("in-progress")}
                      className="px-6 py-2 rounded-md font-semibold text-gray-800 bg-yellow-400 hover:bg-yellow-500 transition-colors"
                    >
                      In Progress
                    </button>

                    {isModalOpen && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
                          <h2 className="text-xl font-bold mb-4 text-gray-800">
                            Confirm Action
                          </h2>
                          <p className="mb-6 text-gray-600">
                            {getConfirmationMessage()}
                          </p>
                          <div className="flex justify-end space-x-4">
                            <button
                              onClick={handleCloseModal}
                              className="px-6 py-2 rounded-md font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleConfirmAction}
                              className="px-6 py-2 rounded-md font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors"
                            >
                              Confirm
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Comment History Section */}
            {currentCommentList?.length > 0 ? (
              <div className="p-4 border border-gray-200 rounded-lg">
                {currentCommentList.map((item, index) => (
                  <div
                    key={index}
                    className="border-b border-gray-100 last:border-b-0 py-3"
                  >
                    {item?.comment && (
                      <p className="text-gray-800 whitespace-pre-wrap">
                        {item.comment}
                      </p>
                    )}
                    {item?.attachments && item.attachments.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2 mt-1">
                          {item.attachments.map((url, attachIndex) => {
                            const isImage = url.match(
                              /\.(jpeg|jpg|gif|png|webp)$/i
                            );

                            return isImage ? (
                              // For images, use the onClick handler to view in a new tab
                              <a
                                key={attachIndex}
                                href={url}
                                onClick={(e) => handleViewImage(e, url)}
                                className="block group relative cursor-pointer"
                              >
                                <img
                                  src={url}
                                  alt={`attachment ${attachIndex + 1}`}
                                  className="h-20 w-20 object-cover rounded-md border hover:opacity-80 transition-opacity"
                                />
                              </a>
                            ) : (
                              // For other files, keep the standard download link behavior
                              <a
                                key={attachIndex}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block group relative"
                              >
                                <div className="h-20 w-20 rounded-md border bg-gray-100 flex flex-col items-center justify-center p-1 text-center text-blue-600 hover:bg-blue-50 transition-colors">
                                  <PaperClipIcon className="h-6 w-6" />
                                  <span className="text-xs mt-1 font-semibold">
                                    View File
                                  </span>
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">{`${item?.date} ${item?.time} - Commented By ${item?.name}`}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 border border-gray-200 rounded-lg text-center text-gray-500">
                <p>No comments yet</p>
              </div>
            )}
          </div>

          <div className="flex justify-end items-center p-4 border-t border-gray-200 sticky bottom-0 bg-white z-10">
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors mr-3"
            >
              Cancel
            </button>
            <button
              onClick={handleFinalSubmit}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AlertStatusModal;
